import { fetchProductsByGoal } from "./FetchProducts.ts";
import { SpcProductAdapter } from "./SpcProductAdapter.ts";
import { CostCalculationService, type ProductCalculationSummary } from "./CostCalculationService.ts";
import { ProductSelectionService, type NoneProductConfig } from "./ProductSelectionService.ts";
import { nonProduct } from "./nonProduct.ts";
import { AvailableGoal, ProductType, type AppProduct, type AvailableGoal as AvailableGoalType } from "@shared/interfaces/product";
import type { Products } from "../../types/calculationProduct.ts";
import type {
    SelectedProducts,
    ProductsByGoal,
} from "../../types/product";
import { createLogger } from "../logger.ts";

const logger = createLogger("ProductManager");

export class ProductManager {
    private static _instance: ProductManager | null = null;

    private readonly availableGoals: AvailableGoalType[] = Object.values(AvailableGoal);
    private readonly noneProducts: Record<AvailableGoalType, NoneProductConfig[]> = {
        [AvailableGoal.BASIC]: [{ type: ProductType.ACCESSORY, default: true }],
        [AvailableGoal.ADVANCED]: [{ type: ProductType.ACCESSORY, default: true }],
        [AvailableGoal.HIGHEND]: [],
    };

    private readonly products: ProductsByGoal = { basic: [], advanced: [], highend: [] };
    private productsStore: Products = [];

    private readonly spcAdapter = new SpcProductAdapter();
    private readonly calcService = new CostCalculationService();
    private readonly selectionService = new ProductSelectionService();

    private constructor() {}

    public static getInstance(): ProductManager {
        if (!ProductManager._instance) {
            ProductManager._instance = new ProductManager();
        }
        return ProductManager._instance;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Initialisation
    // ─────────────────────────────────────────────────────────────────────────

    public async initProducts(initialProducts?: AppProduct[]): Promise<void> {
        initialProducts = initialProducts || await fetchProductsByGoal('basic,advanced,highend');
        this.resetProductCollections();

        logger.debug('Fetched initial products', { initialProducts });

        const seenProducts = new Set<string>();

        initialProducts?.forEach((product: AppProduct) => {
            const goal = this.getGoalFromProduct(product);
            if (!goal) return;

            const dedupeKey = `${goal}:${product.id}`;
            if (seenProducts.has(dedupeKey)) return;

            seenProducts.add(dedupeKey);
            this.products[goal].push(product);
        });

        this.availableGoals.forEach(goal => {
            this.noneProducts[goal].forEach(noneProduct => {
                if (noneProduct.default) {
                    this.products[goal].unshift({ ...nonProduct, type: noneProduct.type });
                } else {
                    this.products[goal].push({ ...nonProduct, type: noneProduct.type });
                }
            });
        });

        logger.debug('products', { products: this.products });

        this.productsStore = this.spcAdapter.buildProductStore(this.products);
        this.spcAdapter.initConfiguratorProductsStore(this.productsStore);
    }

    private resetProductCollections(): void {
        this.availableGoals.forEach(goal => {
            this.products[goal].length = 0;
        });
        this.productsStore = [];
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Product queries
    // ─────────────────────────────────────────────────────────────────────────

    public getProductsByGoal(goal: AvailableGoalType): AppProduct[] {
        return this.products[goal] || [];
    }

    public hasGoalNonProduct(goal: AvailableGoalType, type: string): boolean {
        return this.products[goal].find(
            product => product.type === type && product.id === 0,
        ) !== undefined;
    }

    public getProductsByTypeAndGoal(type: string, goal: AvailableGoalType): Record<AvailableGoalType, AppProduct> {
        if (!this.availableGoals.includes(goal)) {
            return {} as Record<AvailableGoalType, AppProduct>;
        }

        const result = {} as Record<AvailableGoalType, AppProduct>;
        const currentGoalIndex = this.availableGoals.indexOf(goal);
        const indexArray =
            currentGoalIndex < this.availableGoals.length - 1
                ? [currentGoalIndex, currentGoalIndex + 1]
                : [currentGoalIndex - 1, currentGoalIndex];

        indexArray.forEach(index => {
            this.products[this.availableGoals[index]].forEach((product: AppProduct) => {
                if (product.id !== 0 && product.type === type) {
                    result[this.availableGoals[index]] = product;
                }
            });
        });

        return result;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Selection state (delegated to ProductSelectionService)
    // ─────────────────────────────────────────────────────────────────────────

    public setSelectedProducts(selectedProducts: SelectedProducts): void {
        this.selectionService.setSelectedProducts(selectedProducts);
    }

    public setSelectedProductsByGoal(goal: AvailableGoalType): SelectedProducts {
        return this.selectionService.selectDefaultsForGoal(
            this.products[goal],
            this.noneProducts[goal],
        );
    }

    public getSelectedProducts(): SelectedProducts {
        return this.selectionService.getSelectedProducts();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Cost calculation (delegated to CostCalculationService)
    // ─────────────────────────────────────────────────────────────────────────

    public getCostsCalculation(
        selectedProducts: SelectedProducts,
        moduleCount: number,
        kwPerYearAndModule: number,
        powerConsumption: number,
        costsPerKwh: number,
        address: AddressWithPrediction,
    ): ProductCalculationSummary {
        this.selectionService.setSelectedProducts(selectedProducts);
        return this.calcService.getCostsCalculation(
            selectedProducts,
            this.productsStore,
            moduleCount,
            kwPerYearAndModule,
            powerConsumption,
            costsPerKwh,
            address,
        );
    }

    public getFinancingTable(summaryStore: ProductCalculationSummary) {
        return this.calcService.getFinancingTable(summaryStore);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Private helpers
    // ─────────────────────────────────────────────────────────────────────────

    private getGoalFromProduct(product: AppProduct): AvailableGoalType | undefined {
        for (const tag of this.availableGoals) {
            if (product.tags?.includes(tag)) {
                return tag;
            }
        }
        return undefined;
    }
}

// Singleton instance for global use (default export to simplify imports)
export default ProductManager.getInstance();